import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Clock } from "lucide-react";

function diffParts(target) {
  const now = new Date();
  let ms = target - now;
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, expired: ms === 0 };
}

/**
 * Slim fixed-top announcement ribbon. Always visible, non-dismissible.
 * Height ~44px on desktop, ~40px on mobile. Header sits below via top offset.
 */
export default function AnnouncementRibbon({ data }) {
  const [t, setT] = useState(() => diffParts(new Date(data.dateISO)));
  const [expired, setExpired] = useState(() =>
    data.endISO ? new Date() >= new Date(data.endISO) : false
  );

  useEffect(() => {
    const id = setInterval(() => {
      setT(diffParts(new Date(data.dateISO)));
      if (data.endISO && new Date() >= new Date(data.endISO)) {
        setExpired(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [data.dateISO, data.endISO]);

  if (expired) return null;

  const timeStr = t.expired
    ? "In corso"
    : `${t.days}gg ${String(t.hours).padStart(2, "0")}h ${String(t.minutes).padStart(2, "0")}m ${String(t.seconds).padStart(2, "0")}s`;

  return (
    <motion.div
      data-testid="announcement-ribbon"
      initial={{ y: -50 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-[60] h-10 md:h-11 bg-[#1A1A1F]/95 backdrop-blur-md border-b border-accent/60 overflow-hidden"
    >
      {/* diagonal caution stripes background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.09] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, #FACC15 0, #FACC15 10px, transparent 10px, transparent 30px)",
          animation: "ribbonStripes 22s linear infinite",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(250,204,21,0.06),transparent_70%)] pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto h-full px-4 md:px-8 flex items-center justify-between gap-3 md:gap-6">
        {/* LEFT — icon + label */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0 shrink">
          <span className="relative flex items-center justify-center w-5 h-5 md:w-6 md:h-6 border border-accent/50 shrink-0">
            <AlertTriangle size={10} className="text-accent" />
            <span className="absolute inset-0 border border-accent/40 animate-pulse" />
          </span>
          <span className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-accent whitespace-nowrap">
            Annuncio ufficiale
          </span>
        </div>

        {/* CENTER — message (hidden on very small screens) */}
        <div className="hidden md:flex items-center gap-4 flex-1 min-w-0 justify-center">
          <span className="font-body text-[10px] uppercase tracking-[0.25em] text-white/85 truncate">
            {data.kicker}
          </span>
          <span className="w-1 h-1 rounded-full bg-white/25 shrink-0" />
          <span className="font-heading text-xs md:text-sm uppercase tracking-[0.15em] text-white whitespace-nowrap">
            Aggiornamento sito & store — {data.dateLabel}
          </span>
          <span className="w-1 h-1 rounded-full bg-white/25 shrink-0" />
          <span className="inline-flex items-center gap-1.5 font-body text-[10px] uppercase tracking-[0.2em] text-white/70 shrink-0">
            <Clock size={11} className="text-white/50" />
            {data.duration}
          </span>
        </div>

        {/* Mobile compact center */}
        <div className="md:hidden flex-1 text-center min-w-0">
          <span className="font-heading text-[11px] uppercase tracking-[0.15em] text-white whitespace-nowrap">
            {data.dateLabel} · 45min
          </span>
        </div>

        {/* RIGHT — live countdown */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          <span className="font-body text-[10px] md:text-xs uppercase tracking-[0.2em] text-accent whitespace-nowrap">
            {timeStr}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes ribbonStripes {
          from { background-position: 0 0; }
          to { background-position: -400px 0; }
        }
      `}</style>
    </motion.div>
  );
}
